/*
本文の「第5章」「4本」「30 分」のような、数と単位 (と前に付く「第」) を折り返さない 1 つの塊 (span.md-nowrap) にする。「1 日 1 個」のように続く数と単位は、続きもまとめて 1 つにする。
Chrome の文節折り (word-break auto-phrase) は「の第 / 5章」のように「第」と数の間で折ることがある。
見た目 (折り返さない) は lg-prose.css が付ける。lg の外では span は何も変えない。字は 1 字も変えない。
コードと、すでに塊になった物 (md-nowrap) の中は触らない
*/

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

// select と option などは子に span を置けない (ブラウザーが HTML の解析で span を落とし、SSR と hydration が食い違う)
const SKIP = new Set([
  "pre", "code", "kbd", "svg", "style", "script", "textarea", "math",
  "select", "option", "optgroup", "datalist", "title", "iframe", "noscript", "template",
]);
const UNITS = "項目|ページ|章|節|部|回|本|問|分|秒|時間|日|週|か月|ヶ月|カ月|月|年|個|件|人|行|字|枚|段|歩|番|点|つ|万円|円|KB|MB|GB|%";
// 「36〜68 時間」「3〜5 時間」のような幅は、「〜」の後ろで折らず幅ごと 1 つの塊にする (390 幅で「36〜 / 68 時間」と割れた)
const RANGE_HEAD = "(?:[0-9０-９]+(?:[.,][0-9]+)? ?[〜~～] ?)?";
const ONE = `第?${RANGE_HEAD}[0-9０-９]+(?:[.,][0-9]+)? ?(?:${UNITS})`;
// 「1 日 1 個」「2026 年 10 月 16 日」のように数と単位が続くときは、続きの間でも折らない 1 つの塊にする (「1 日 / 1 個ずつ」の割れを避ける)
// 英字や閉じ括弧の後ろの「空白 + 助詞」(「Slack に似た」「(⌘ + Shift + P) から」) は、前の語ごと 1 つの塊にする。
// 空白の後ろで折れて、助詞が行頭に来ていた (2026-10-17 r6、表のセル「Slack / に似た UI」)。
// 塊を空白と助詞だけにすると、Chrome は塊の手前 (空白の前) で折るので効かなかった。前の語は 24 字までにし、長い英字の連なりは塊にしない
// 「ではなく」「とは」のように助詞が続くときは続きもまとめる。仮名の途中で塊を切ると、そこが新しい折れ目になる
const LATIN_PARTICLE = "(?:(?<![A-Za-z0-9.+#-])[A-Za-z0-9][A-Za-z0-9.+#-]{0,23}|[)）]) (?:では|には|とは|でも|にも|へは|からの|までの|から|まで|より|[のをにがとでへはも])(?![ぁ-ゖ])";
// 「月1回」「週2回」のように、期間の字が数の前に付く形も 1 つの塊にする (「月 / 1回」と割れた)
const PERIOD_ONE = `(?:毎?[月週年日])?${ONE}`;
// 1 つの語として読む仮名の連なり。Chrome の文節折りが「気に / なる」と割った (basic 48、390 幅)
const KEEP_KANA_WORDS = "気にな[るりっら]|気にし[てたな]|気にせ";
const NUMBER_UNIT = new RegExp(`${PERIOD_ONE}(?: ?${ONE})*|${LATIN_PARTICLE}|${KEEP_KANA_WORDS}`, "g");

function hasNowrapClass(node: HastNode): boolean {
  const cls = node.properties?.className;
  return Array.isArray(cls) ? cls.includes("md-nowrap") : cls === "md-nowrap";
}

function splitText(value: string): HastNode[] | null {
  NUMBER_UNIT.lastIndex = 0;
  if (!NUMBER_UNIT.test(value)) return null;
  NUMBER_UNIT.lastIndex = 0;
  const out: HastNode[] = [];
  let from = 0;
  for (const m of value.matchAll(NUMBER_UNIT)) {
    const start = m.index ?? 0;
    if (start > from) out.push({ type: "text", value: value.slice(from, start) });
    out.push({
      type: "element",
      tagName: "span",
      properties: { className: ["md-nowrap"] },
      children: [{ type: "text", value: m[0] }],
    });
    from = start + m[0].length;
  }
  if (from < value.length) out.push({ type: "text", value: value.slice(from) });
  return out;
}

function walk(node: HastNode): void {
  if (!node.children) return;
  const next: HastNode[] = [];
  let changed = false;
  for (const child of node.children) {
    if (child.type === "text") {
      const parts = splitText(child.value ?? "");
      if (parts) {
        next.push(...parts);
        changed = true;
        continue;
      }
    } else if (child.type === "element" && !SKIP.has(child.tagName ?? "") && !hasNowrapClass(child)) {
      walk(child);
    }
    next.push(child);
  }
  if (changed) node.children = next;
}

export function rehypeKeepNumberUnits() {
  return (tree: HastNode) => {
    walk(tree);
  };
}
